import { object, string, number, array, boolean, mixed, ObjectSchema, InferType, date } from "yup";
import { EditarEntradaDeNotasCompraContract } from "../contracts/editar-entrada-de-notas-compra.contracts";

export const editarEntradaDeNotasCompraSchema: ObjectSchema<EditarEntradaDeNotasCompraContract> = object({
  isClient: boolean().required("Informe se é para cliente ou obra!"),
  clientId: string().nullable().optional(),
  workId: string().nullable().optional(),
  supplierId: string().when("newRegister", {
    is: false,
    then: (schema) => schema.required("Informe um Fornecedor!"),
    otherwise: (schema) => schema.optional(),
  }),
  // Dados da nota fiscal
  invoiceNumber: string().required("Número da nota é obrigatório!"),
  series: string().required("Série da nota é obrigatória!"),
  sheet: string().nullable().optional(),
  issueDate: date().required("Data de emissão é obrigatória!"),
  entryDate: date().required("Data de entrada é obrigatória!"),
  exitDate: date()
    .nullable()
    .optional()
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    }),
  exitTime: string().nullable().optional(),
  accessKey: string().required("Chave de acesso é obrigatória!"),
  authorizationProtocol: string().optional(),
  operationNature: string().required("Natureza da operação é obrigatória!"),
  operationType: string().optional(),
  cfop: string().required("CFOP é obrigatório!"),
  notes: string().optional(),
  totalValue: number()
    .typeError("Valor total deve ser um número!")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
        if (!cleaned) return undefined;
        const valorNumber = Number(cleaned);
        return isNaN(valorNumber) ? undefined : valorNumber;
      }
      return value;
    })
    .required("Valor total é obrigatório!")
    .max(99999999.99, "Valor total muito alto!")
    .default(0),
  productsValue: number()
    .typeError("Valor dos produtos deve ser um número!")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
        if (!cleaned) return undefined;
        const valorNumber = Number(cleaned);
        return isNaN(valorNumber) ? undefined : valorNumber;
      }
      return value;
    })
    .max(99999999.99, "Valor dos produtos muito alto!")
    .optional(),
  discountValue: number()
    .typeError("Valor do desconto deve ser um número!")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
        if (!cleaned) return undefined;
        const valorNumber = Number(cleaned);
        return isNaN(valorNumber) ? undefined : valorNumber;
      }
      return value;
    })
    .max(99999999.99, "Valor do desconto muito alto!")
    .optional(),
  freightValue: number()
    .typeError("Valor do frete deve ser um número!")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
        if (!cleaned) return undefined;
        const valorNumber = Number(cleaned);
        return isNaN(valorNumber) ? undefined : valorNumber;
      }
      return value;
    })
    .max(99999999.99, "Valor do frete muito alto!")
    .optional(),
  insuranceValue: number()
    .typeError("Valor do seguro deve ser um número!")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
        if (!cleaned) return undefined;
        const valorNumber = Number(cleaned);
        return isNaN(valorNumber) ? undefined : valorNumber;
      }
      return value;
    })
    .max(99999999.99, "Valor do seguro muito alto!")
    .optional(),
  otherExpensesValue: number().typeError("Outras despesas deve ser um número!").optional(),
  taxesValue: number()
    .typeError("Valor dos impostos deve ser um número!")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
        if (!cleaned) return undefined;
        const valorNumber = Number(cleaned);
        return isNaN(valorNumber) ? undefined : valorNumber;
      }
      return value;
    })
    .max(99999999.99, "Valor dos impostos muito alto!")
    .optional(),

  // Transportador / Volumes Transportados
  freightResponsibility: string().nullable().optional(),
  volumesQuantity: number().typeError("Quantidade de volumes deve ser um número!").nullable().optional(),
  grossWeight: number().typeError("Peso bruto deve ser um número!").nullable().optional(),
  netWeight: number().typeError("Peso líquido deve ser um número!").nullable().optional(),

  // Duplicatas
  duplicateNumber: string().nullable().optional(),
  duplicateDueDate: date().nullable().optional(),
  duplicateValue: number().nullable().default(0)
    .typeError("Valor da duplicata deve ser um número!")
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
        if (!cleaned) return undefined;
        const valorNumber = Number(cleaned);
        return isNaN(valorNumber) ? undefined : valorNumber;
      }
      return value;
    })
    .max(99999999.99, "Valor da duplicata muito alto!")
    .optional(),

  // Itens da nota fiscal de compra
  items: array().of(
    object({
      id: string().required("E obrigatório informar o id dos itens de compra!"),
      productCode: string().required("Código do produto é obrigatório!"),
      productDescription: string().optional(),
      quantity: number().typeError("Quantidade deve ser um número!").required("Quantidade é obrigatória!"),
      unitPrice: number()
        .typeError("Preço unitário deve ser um número!")
        .transform((value, originalValue) => {
          if (typeof originalValue === "string") {
            const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
            if (!cleaned) return undefined;
            const valorNumber = Number(cleaned);
            return isNaN(valorNumber) ? undefined : valorNumber;
          }
          return value;
        })
        .required("Preço unitário é obrigatório!")
        .max(99999999.99, "Preço unitário muito alto!")
        .default(0),
      totalPrice: number()
        .typeError("Preço total deve ser um número!")
        .transform((value, originalValue) => {
          if (typeof originalValue === "string") {
            const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
            if (!cleaned) return undefined;
            const valorNumber = Number(cleaned);
            return isNaN(valorNumber) ? undefined : valorNumber;
          }
          return value;
        })
        .required("Preço total é obrigatório!")
        .max(99999999.99, "Preço total muito alto!")
        .default(0),
      ncm: string().optional(),
      cfop: string().optional(),
      unit: string().optional(),
      ean: string().optional(),
      cst: string().optional(),
      ipi: number().typeError("IPI deve ser um número!").optional(),
      icms: number().typeError("ICMS deve ser um número!").optional(),
      pis: number().typeError("PIS deve ser um número!").optional(),
      cofins: number().typeError("COFINS deve ser um número!").optional(),
    })
  ).min(1, "É obrigatório informar ao menos um item!").required("Itens da nota são obrigatórios!"),
});

export type EditarEntradaDeNotasCompraFormData = InferType<typeof editarEntradaDeNotasCompraSchema>;